/** SUNTRONIX ONE — Zero-Touch CRM Automation (Config Studio · Experience).
 *  Leads / Qualifications / Opportunities are generated automatically from
 *  Quotations. This screen is admin-only: toggles, backfill, reconciliation,
 *  exceptions. Normal sales users never maintain CRM stages. */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Cog, RefreshCw, PlayCircle, AlertTriangle, CheckCircle2 } from "lucide-react";

const TOGGLES = [
  ["auto_lead", "Auto Lead"], ["auto_qualification", "Auto Qualification"],
  ["auto_opportunity", "Auto Opportunity"], ["auto_stage_sync", "Auto Stage Sync"],
  ["auto_value_sync", "Auto Value Sync"], ["auto_owner_sync", "Auto Owner Sync"],
  ["auto_po_sync", "Auto PO Sync"], ["auto_order_sync", "Auto Order Sync"],
  ["auto_lost_sync", "Auto Lost Sync"], ["auto_reconciliation", "Auto Reconciliation"],
];

export default function CrmAutomation() {
  const [cfg, setCfg] = useState(null);
  const [recon, setRecon] = useState(null);
  const [bf, setBf] = useState(null);
  const [exc, setExc] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadAll = () => {
    api.get("/crm-auto/config").then(({ data }) => setCfg(data)).catch(() => {});
    api.get("/crm-auto/reconcile").then(({ data }) => setRecon(data)).catch(() => {});
    api.get("/crm-auto/backfill/status").then(({ data }) => setBf(data)).catch(() => {});
    api.get("/crm-auto/exceptions").then(({ data }) => setExc(data)).catch(() => {});
  };
  useEffect(() => { loadAll(); }, []);

  const toggle = async (key) => {
    const next = { ...cfg, [key]: !cfg[key] };
    setCfg(next);
    try { await api.put("/crm-auto/config", { values: { [key]: next[key] } }); toast.success("Saved"); }
    catch { toast.error("Save failed"); loadAll(); }
  };

  const runBackfill = async () => {
    setBusy(true);
    try {
      await api.post("/crm-auto/backfill");
      toast.success("Backfill started");
      const poll = setInterval(async () => {
        const { data } = await api.get("/crm-auto/backfill/status");
        setBf(data);
        if (!data.running) { clearInterval(poll); loadAll(); }
      }, 2000);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const repair = async () => {
    setBusy(true);
    try { await api.post("/crm-auto/reconcile/repair"); toast.success("Repair started — re-syncing all quotes"); runPoll(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const runPoll = () => {
    const poll = setInterval(async () => {
      const { data } = await api.get("/crm-auto/backfill/status");
      setBf(data);
      if (!data.running) { clearInterval(poll); loadAll(); }
    }, 2000);
  };

  if (!cfg) return <div className="p-8 text-sm text-slate-400">Loading…</div>;

  return (
    <div className="max-w-[1000px] mx-auto px-4 py-6" data-testid="crm-automation-page">
      <div className="flex items-center gap-2 mb-1">
        <Cog className="h-5 w-5 text-indigo-600" />
        <h1 className="text-lg font-semibold text-slate-900">Zero-Touch CRM Automation</h1>
      </div>
      <p className="text-[13px] text-slate-500 mb-5">
        Leads, Qualifications and Opportunities are created and kept in sync automatically from your Quotations.
        Your sales team never has to maintain these — they just quote.
      </p>

      {/* Toggles */}
      <div className="border border-slate-200 rounded-lg bg-white p-4 mb-5">
        <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-3">Automation switches</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {TOGGLES.map(([k, label]) => (
            <button key={k} onClick={() => toggle(k)} data-testid={`crm-auto-toggle-${k}`}
              className={`flex items-center justify-between text-[13px] rounded-md px-3 py-2 border transition-colors ${
                cfg[k] ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-slate-50 border-slate-200 text-slate-500"}`}>
              {label}
              <span className={`ml-2 text-[10px] font-semibold ${cfg[k] ? "text-emerald-600" : "text-slate-400"}`}>{cfg[k] ? "ON" : "OFF"}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Reconciliation */}
      <div className="border border-slate-200 rounded-lg bg-white p-4 mb-5">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[11px] uppercase tracking-wide text-slate-400">CRM lineage reconciliation</div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={loadAll} data-testid="crm-auto-refresh"><RefreshCw className="h-3.5 w-3.5 mr-1.5" />Refresh</Button>
            <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={repair} disabled={busy} data-testid="crm-auto-repair">Repair safe mismatches</Button>
          </div>
        </div>
        {recon ? (
          <div>
            <div className="flex items-center gap-2 mb-3 text-[13px]">
              {recon.clean ? <><CheckCircle2 className="h-4 w-4 text-emerald-600" /><span className="text-emerald-700 font-medium">All {recon.quote_count} quotes reconciled</span></>
                : <><AlertTriangle className="h-4 w-4 text-amber-600" /><span className="text-amber-700 font-medium">{recon.quote_count} quotes — gaps found</span></>}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="crm-auto-recon-tiles">
              {[["Missing Lead", recon.missing_lead], ["Missing Qualification", recon.missing_qualification],
                ["Missing Opportunity", recon.missing_opportunity], ["Stage mismatch", recon.stage_mismatch],
                ["Value mismatch", recon.value_mismatch], ["Duplicate opportunity", recon.duplicate_opportunity]].map(([l, n]) => (
                <div key={l} className={`rounded-md border px-3 py-2 ${n ? "border-amber-200 bg-amber-50" : "border-slate-200 bg-slate-50"}`}>
                  <div className={`text-lg font-semibold ${n ? "text-amber-700" : "text-slate-700"}`}>{n}</div>
                  <div className="text-[11px] text-slate-500">{l}</div>
                </div>
              ))}
            </div>
          </div>
        ) : <div className="text-[13px] text-slate-400">Loading reconciliation…</div>}
      </div>

      {/* Backfill */}
      <div className="border border-slate-200 rounded-lg bg-white p-4 mb-5">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[11px] uppercase tracking-wide text-slate-400">Historical backfill</div>
          <Button size="sm" className="bg-slate-900 hover:bg-slate-800 text-white" onClick={runBackfill} disabled={busy || bf?.running} data-testid="crm-auto-backfill">
            <PlayCircle className="h-3.5 w-3.5 mr-1.5" />{bf?.running ? "Running…" : "Run backfill"}
          </Button>
        </div>
        {bf && (
          <div className="text-[13px] text-slate-600" data-testid="crm-auto-backfill-status">
            {bf.running ? `Processing ${bf.done} of ${bf.total}…` : bf.finished_at ? `Last run: ${bf.done} quotes processed, ${bf.created} lineages created.` : "Not run yet."}
          </div>
        )}
      </div>

      {/* Exceptions */}
      <div className="border border-slate-200 rounded-lg bg-white p-4" data-testid="crm-auto-exceptions">
        <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-3">Automation exceptions</div>
        {exc && exc.count === 0 ? (
          <div className="text-[13px] text-emerald-700 flex items-center gap-2" data-testid="crm-auto-exceptions-empty"><CheckCircle2 className="h-4 w-4" />No open exceptions.</div>
        ) : exc ? (
          <div className="space-y-1.5" data-testid="crm-auto-exceptions-list">
            {exc.items.map((e) => (
              <div key={e.id} className="text-[12px] text-rose-700 bg-rose-50 border border-rose-100 rounded px-2 py-1">
                {e.quote_id}: {e.error}
              </div>
            ))}
          </div>
        ) : <div className="text-[13px] text-slate-400">Loading…</div>}
      </div>
    </div>
  );
}
